import React, { useState } from "react";

import {
  IonButton,
  IonIcon,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonInput,
  IonButtons,
  IonTextarea,
  IonSpinner,
  IonAlert,
  IonCheckbox,
  useIonRouter,
} from "@ionic/react";

import {
  closeOutline,
  imageOutline,
  addCircleOutline,
  cameraOutline,
  lockClosed,
  camera,
} from "ionicons/icons";

import { useDispatch } from "react-redux";

import { createHashtag } from "../../api/requests/hashtag";

import { formatHashtag } from "../../utils/formatHashtag";

import { getTrends } from "../../redux/actions/postActions";

import InviteFriendsModal from "./InviteFriendModal";

import "./CreateCommunity.css"; // Import the new CSS file

export const CreateCommunityButton = ({ screen = "mobile" }) => {
  const [isCreateCommunityOpen, setIsCreateCommunityOpen] = useState(false);
  return screen === "mobile" ? (
    <>
      {/* Removed the div wrapper with flex-end */}
      <IonButton
        fill="clear"
        onClick={() => setIsCreateCommunityOpen(true)}
        style={{
          margin: 0,
          "--padding-start": "0",
          "--padding-end": "0",
          height: "auto",
        }}
      >
        <IonIcon
          icon={addCircleOutline}
          style={{ color: "#d80621", fontSize: "32px" }} // Increased size to match Info icon
        />
      </IonButton>
      <CreateCommunityModal
        isOpen={isCreateCommunityOpen}
        onClose={() => setIsCreateCommunityOpen(false)}
      />
    </>
  ) : (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <IonButton
        fill="clear"
        onClick={() => setIsCreateCommunityOpen(true)}
        style={{
          margin: 0,
          height: "unset",
          "--padding-top": "0",
          "--padding-bottom": "0",
          "--padding-start": "4px",
          "--padding-end": "4px",
        }}
      >
        <IonIcon
          icon={addCircleOutline}
          style={{ color: "var(--main-color)", fontSize: "1rem" }}
        />
      </IonButton>

      <CreateCommunityModal
        isOpen={isCreateCommunityOpen}
        onClose={() => setIsCreateCommunityOpen(false)}
      />
    </div>
  );
};

const CreateCommunityModal = ({ isOpen, onClose }) => {
  const router = useIonRouter();

  const dispatch = useDispatch();

  const [communityName, setCommunityName] = useState("");

  const [communityDescription, setCommunityDescription] = useState("");

  const [selectedImage, setSelectedImage] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  const [showAlert, setShowAlert] = useState(false);

  const [alertMessage, setAlertMessage] = useState("");

  const [isPrivate, setIsPrivate] = useState(false);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [rules, setRules] = useState([
    "Be respectful to fellow members.",
    "No spam or repetitive self-promotion.",
    "Keep discussions relevant to the community topic.",
  ]);
  const [hashtag, setHashtag] = useState("");

  const [creationStatus, setCreationStatus] = useState(null);

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (file) setSelectedImage(file);
  };

  const handleCreateCommunity = async () => {
    if (!communityName.trim()) return;

    setIsLoading(true);

    const hashtagName = formatHashtag(communityName.trim().toLowerCase());

    try {
      const formData = new FormData();

      formData.append("name", hashtagName);

      formData.append("displayName", communityName.trim());

      formData.append("description", communityDescription.trim());

      formData.append("isPrivate", isPrivate);
      formData.append("rules", JSON.stringify(rules));
      if (selectedImage) formData.append("image", selectedImage);

      const createResponse = await createHashtag(formData);

      if (createResponse.status === 201) {
        setCreationStatus("success");

        setAlertMessage("Community created successfully!");

        setShowAlert(true);

        dispatch(getTrends());
      } else if (createResponse.status === 409) {
        setCreationStatus("exists");

        setAlertMessage("This community already exists! Taking you there...");

        setShowAlert(true);
      } else if (createResponse.status === 403) {
        setCreationStatus("reserved");

        setAlertMessage(
          "This is a system-default community and cannot be created privately.",
        );

        setShowAlert(true);
      } else {
        setCreationStatus("error");

        setAlertMessage(
          createResponse.data?.message || "Failed to create community",
        );

        setShowAlert(true);
      }
    } catch (error) {
      setCreationStatus("error");

      setAlertMessage("Failed to create community. Please try again.");

      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlertDismiss = () => {
    setShowAlert(false);

    if (creationStatus === "reserved" || creationStatus === "error") {
      setCreationStatus(null);

      return;
    }

    setAlertMessage("");

    onClose();

    setHashtag(communityName);

    setCommunityDescription("");

    setSelectedImage(null);

    if (isPrivate) setShowInviteModal(true);
    else navigateToHashtag();

    setCreationStatus(null);
  };

  const navigateToHashtag = () => {
    const hashtagName = formatHashtag(communityName.trim().toLowerCase()).slice(
      1,
    );

    router.push(`/hashtag/${hashtagName}`);
  };
  const isFormValid = communityName.trim().length > 0;
  return (
    <div className="create-community-modal poppins">
      <IonModal isOpen={isOpen} onDidDismiss={() => !isLoading && onClose()}>
        <IonHeader className="">
          <IonToolbar className="modal-header-toolbar">
            <IonTitle className="modal-title">Create Community</IonTitle>

            <IonButtons slot="end">
              <IonButton onClick={onClose} disabled={isLoading}>
                <IonIcon icon={closeOutline} className="close-icon" />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding community-modal-content">
          <div className="modal-container">
            {/* Name Input */}

            <div>
              <label className="input-label">Community Name</label>

              <div className="input-wrapper">
                <IonInput
                  className="community-input"
                  value={communityName}
                  onIonInput={(e) => setCommunityName(e.detail.value)}
                  placeholder="Enter community name"
                />
              </div>
            </div>

            {/* Description Input */}

            <div>
              <label className="input-label">Description</label>

              <div className="input-wrapper">
                <IonTextarea
                  className="community-textarea"
                  value={communityDescription}
                  onIonInput={(e) => setCommunityDescription(e.detail.value)}
                  placeholder="Add a description for your community..."
                  rows={3}
                />
              </div>
            </div>
            <div>
              <label className="input-label">Community Rules</label>

              <div className="rules-container">
                {rules.map((rule, index) => (
                  <div key={index} className="rule-item">
                    <IonInput
                      className="rule-input"
                      value={rule}
                      onIonInput={(e) => {
                        const newRules = [...rules];
                        newRules[index] = e.detail.value;
                        setRules(newRules);
                      }}
                      placeholder={`Rule ${index + 1}`}
                    />

                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={() => {
                        const newRules = rules.filter((_, i) => i !== index);
                        setRules(newRules);
                      }}
                      disabled={rules.length <= 1}
                    >
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </div>
                ))}

                {rules.length < 10 && (
                  <IonButton
                    className="add-rule-btn"
                    fill="clear"
                    onClick={() => setRules([...rules, ""])}
                  >
                    <IonIcon icon={addCircleOutline} /> Add Rule
                  </IonButton>
                )}
              </div>
            </div>
            {/* Logo Upload */}

            <div>
              {/* <label className="input-label">Community Logo</label> */}

              <input
                type="file"
                id="commImg"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: "none" }}
              />

              <label htmlFor="commImg" className="logo-upload-box">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div className="upload-icon-container">
                    <IonIcon icon={imageOutline} style={{ color: "#d80621" }} />
                  </div>

                  <span
                    className="poetsen"
                    style={{ fontWeight: "400", color: "#000" }}
                  >
                    Upload logo
                  </span>
                </div>

                <div className="camera-badge">
                  <IonIcon icon={camera} style={{ color: "#fff" }} />
                </div>
              </label>
              <span style={{ fontSize: "12px", color: "#6B7280" }}>
                {selectedImage ? selectedImage.name : ""}
              </span>
            </div>

            {/* Privacy */}

            <div
              style={{
                paddingBottom: "10px",
                borderBottom: "1px solid #F2F2F2",
              }}
            >
              <label
                className="input-label"
                style={{ marginTop: "20px", marginBottom: "20px" }}
              >
                Privacy
              </label>

              <div className="privacy-item-container">
                <div className="privacy-info">
                  <div className="lock-icon-bg">
                    <IonIcon icon={lockClosed} style={{ color: "#fff" }} />
                  </div>

                  <div>
                    <div
                      style={{ fontWeight: "400", color: "#000" }}
                      className="poetsen"
                    >
                      Private
                    </div>

                    <div
                      style={{
                        color: "#9ca3af",
                        fontSize: "18px",
                      }}
                    >
                      A private community
                    </div>
                  </div>
                </div>

                <IonCheckbox
                  className="privacy-checkbox"
                  checked={isPrivate}
                  onIonChange={(e) => setIsPrivate(e.detail.checked)}
                />
              </div>
            </div>

            {/* Buttons */}

            <div className="button-group">
              <IonButton
                className="submit-button"
                expand="block"
                onClick={handleCreateCommunity}
                disabled={!isFormValid || isLoading}
                style={{
                  /* Toggles between muted pink and solid red based on form validity */

                  "--background": isFormValid ? "#d80621" : "#f1a1ab",
                }}
              >
                {isLoading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  "Create Community"
                )}
              </IonButton>

              <IonButton
                className="cancel-button"
                expand="block"
                fill="clear"
                onClick={onClose}
              >
                Cancel
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={showAlert}
        onDidDismiss={handleAlertDismiss}
        header="Community"
        message={alertMessage}
        buttons={["OK"]}
      />

      <InviteFriendsModal
        isOpen={showInviteModal}
        onDismiss={() => {
          setShowInviteModal(false);

          navigateToHashtag();
        }}
        tag={hashtag}
      />
    </div>
  );
};

export default CreateCommunityModal;
